import { ApiPointsAuditLog, ApiPointsAuditProcess } from '@api/interfaces';
import { PointsAuditEventType, PointsAuditProcessType } from '@shared/enums';
import { TFunction } from 'i18next';
import { describeAuditEvent } from './describeAuditEvent';
import { formatSignedPoints } from './movementReasons';

export type ProcessSource = Pick<
  ApiPointsAuditProcess,
  'fileName' | 'processType' | 'summaryEvent' | 'undoableEventCount'
>;

const toNumber = (value: unknown) => (typeof value === 'number' ? value : Number(value ?? 0));

const describeSync = (row: ApiPointsAuditLog, t: TFunction): string => {
  const { bookings, grainTickets, invoices } = row.metadata ?? {};
  const parts = [
    toNumber(invoices) && t($ => $.pointsAudit.source.syncInvoices, { count: toNumber(invoices) }),
    toNumber(bookings) && t($ => $.pointsAudit.source.syncBookings, { count: toNumber(bookings) }),
    toNumber(grainTickets) && t($ => $.pointsAudit.source.syncGrainTickets, {
      count: toNumber(grainTickets),
    }),
  ].filter(Boolean);
  return t($ => $.pointsAudit.source.sync, { written: parts.join(', ') });
};

/** What made the process and what it did, as `<origin>: <result>`, from its summary row. */
const describeOrigin = (process: ProcessSource, t: TFunction): string => {
  const row = process.summaryEvent;
  const before = row.before ?? {};
  const after = row.after ?? {};
  const metadata = row.metadata ?? {};
  const fileName = process.fileName ?? (metadata.fileName as string | undefined);

  if (fileName) {
    if (row.eventType === PointsAuditEventType.ImportDiscarded) {
      return t($ => $.pointsAudit.source.fileRejected, { file: fileName });
    }
    return row.eventType === PointsAuditEventType.ImportCompleted
      ? t($ => $.pointsAudit.source.file, {
        count: toNumber(metadata.rowsProcessed),
        file: fileName,
      })
      : fileName;
  }

  switch (row.eventType) {
    case PointsAuditEventType.SyncCompleted:
      return describeSync(row, t);
    case PointsAuditEventType.RecalculationQueued:
      // A sync that wrote no orders records only its recalculation.
      return process.processType === PointsAuditProcessType.Sync
        ? t($ => $.pointsAudit.source.syncNothingNew)
        : t($ => $.pointsAudit.source.recalculation, {
          count: toNumber(metadata.farmersQueued ?? metadata.rewardsAccountsQueued),
        });
    case PointsAuditEventType.ManualPointsAdjusted:
      return t($ => $.pointsAudit.source.manualPoints, {
        count: Math.abs(toNumber(after.amount)),
        points: formatSignedPoints(toNumber(after.amount)),
      });
    case PointsAuditEventType.TierOverrideSet:
      return after.rankOverride === null || after.rankOverride === undefined
        ? t($ => $.pointsAudit.source.tierCleared)
        : t($ => $.pointsAudit.source.tierSet, { rank: after.rankOverride });
    case PointsAuditEventType.ExclusionChanged:
      return after.excludeFromLoyaltyProgram
        ? t($ => $.pointsAudit.source.excluded)
        : t($ => $.pointsAudit.source.included);
    case PointsAuditEventType.MembershipChanged: {
      const isEnergyChange = before.energy !== after.energy;
      const program = isEnergyChange
        ? t($ => $.pointsAudit.programs.energy)
        : t($ => $.pointsAudit.programs.agronomy);
      const isJoining = isEnergyChange ? after.energy : after.agronomy;
      return isJoining
        ? t($ => $.pointsAudit.source.membershipJoined, { program })
        : t($ => $.pointsAudit.source.membershipLeft, { program });
    }
    case PointsAuditEventType.BehaviorCompleted:
      return t($ => $.pointsAudit.source.behaviorCompleted, {
        behavior: metadata.behaviorName ?? '',
      });
    case PointsAuditEventType.BehaviorReversed:
      return t($ => $.pointsAudit.source.behaviorReversed, {
        behavior: metadata.behaviorName ?? '',
      });
    case PointsAuditEventType.PointsExpirationChanged:
      return t($ => $.pointsAudit.source.expiration, {
        from: before.pointsExpirationDate,
        to: after.pointsExpirationDate,
      });
    case PointsAuditEventType.BookingPointsReturned:
      return t($ => $.pointsAudit.source.bookingReturned, {
        count: toNumber(after.pointsReturned),
      });
    default:
      return describeAuditEvent(row, t);
  }
};

/**
 * What the process list shows as its source. A file names the import; anything else names what
 * made it and what it did, counting the further changes a bulk action made.
 */
export const describeProcessSource = (process: ProcessSource, t: TFunction): string => {
  const origin = describeOrigin(process, t);
  const isRun = [PointsAuditProcessType.Import, PointsAuditProcessType.Sync]
    .includes(process.processType);
  return !isRun && process.undoableEventCount > 1
    ? t($ => $.pointsAudit.source.withMoreChanges, {
      count: process.undoableEventCount,
      source: origin,
    })
    : origin;
};
